/**
 * Line lifting: IrLine -> Line + tracks + keys + notes (W1-04 core-ir; DESIGN.md §6.5, §6.12, §6.13, §7.2).
 *
 *  - kind is derived (never stored): Texture "line.png" -> no texture; any other path -> the asset of that path;
 *    `gifEvents: []` on a GIF texture -> `Line.gifAutoLoop`; UI mover = attachUI;
 *  - father index -> parent id, rotateWithFather, isCover (1 = cover; another integer kept in `rpe.isCoverRaw`),
 *    zOrder, Group index -> group, note controls, skewControl, bpmfactor, anchor;
 *  - eventLayers keep their slot structure (`rpe.layerSlots`: null / {} slots without tracks), per-layer
 *    passthrough and explicit-null list keys; every list becomes a track at its layer index;
 *  - extended: present as object / null / absent (`rpe.extended`), typed lists -> tracks, lists Phira ignores for
 *    rendering on the line's kind -> `rpe.ignored` written back in place (writeBack 'extended'), GIF data on a
 *    texture that is not a GIF -> `rpe.ignored` kept only in phiedit.json (writeBack 'sidecar', reported);
 *  - tracks of 'extrapolate' properties whose first key is after beat 0 -> `Track.importedLeadIn`;
 *  - notes: type, raw triples, a non-hold endTime -> `NoteSrc.nonHoldEnd`, above / isFake raw integers,
 *    visibleTime 999999 -> always, hitsound "tap.mp3" / "flick.mp3" / "drag.mp3" -> built-in sound, else an asset.
 * Lifted lines have origin 'import', except a line that carries Lower's neutral padding (a chart exported without
 * phiedit.json and read back, UX-026): it was an editor line, so it is lifted without its padding lists, RPE default
 * controls and default skewControl (pad.ts#unpadEditorLine) and gets origin 'editor' back, and the next export pads
 * it the same way again. Lines Phira re-derives (isGif, numOfNotes) are not stored.
 */

import { Defaults, RPE_VISIBLE_ALWAYS } from '../model/defaults';
import { requiresLeadInKey } from '../model/props';
import type {
  AssetId,
  ControlCurve,
  GroupId,
  Hitsound,
  IgnoredData,
  Key,
  KeyId,
  LayerSlotStyle,
  Line,
  LineId,
  LineProp,
  LineRpeData,
  Note,
  NoteId,
  NoteKind,
  NoteSrc,
  SpeedMode,
  Track,
  TrackId,
  TrackOwner,
  TrackValue,
  TrackValueType,
} from '../model/types';
import { PROP_SPECS } from '../model/props';
import { Beats } from '../time/beat';
import type { Json, Passthrough, RGB } from '../util/types';
import { controlIn, eventJson, EXT_PROPS_IN_ORDER, LAYER_PROPS_IN_ORDER, listKeyOf } from './irShape';
import { liftLineEvents, type LiftKeyData } from './liftTrack';
import { unpadEditorLine } from './pad';
import { tripleEquals, tripleIn } from './triples';
import type { ImportClass, ImportReportEntry, IrEvent, IrExtended, IrLayer, IrLine, IrNote } from './types';

/** Lifting environment shared by a whole chart. */
export interface LiftEnv {
  readonly speedMode: SpeedMode;
  readonly assetIdForPath: (path: string) => AssetId;
  nextId(): number;
  report(
    cls: ImportClass,
    key: string,
    params?: Readonly<Record<string, string | number>>,
    path?: string,
    line?: number,
  ): void;
  readonly tracks: Map<TrackId, Track>;
  readonly keys: Map<KeyId, Key>;
  readonly notes: Map<NoteId, Note>;
}

/** Collects report entries, folding repeats of the same message, path-less and per line. */
export class LiftReport {
  private readonly entries: ImportReportEntry[] = [];

  add(
    cls: ImportClass,
    key: string,
    params?: Readonly<Record<string, string | number>>,
    path?: string,
    line?: number,
  ): void {
    const message = params === undefined ? { key } : { key, params };
    const entry: { -readonly [K in keyof ImportReportEntry]: ImportReportEntry[K] } = { cls, message };
    if (path !== undefined) entry.path = path;
    if (line !== undefined) entry.line = line;
    this.entries.push(entry);
  }

  /** Entries with identical class, message and line folded into one (count = occurrences, first path kept). */
  list(): ImportReportEntry[] {
    const out: { -readonly [K in keyof ImportReportEntry]: ImportReportEntry[K] }[] = [];
    const index = new Map<string, number>();
    for (const e of this.entries) {
      const k = JSON.stringify([e.cls, e.message.key, e.message.params ?? null, e.line ?? null]);
      const at = index.get(k);
      if (at === undefined) {
        index.set(k, out.length);
        out.push({ ...e });
        continue;
      }
      const prev = out[at];
      if (prev !== undefined) prev.count = (prev.count ?? 1) + 1;
    }
    return out;
  }
}

const VALUE_TYPE: Readonly<Record<LineProp, TrackValueType>> = Object.fromEntries(
  Object.values(PROP_SPECS).map((s) => [s.prop, s.valueType]),
) as Record<LineProp, TrackValueType>;

/** Creates a track and its keys; returns the track id. */
export function addTrack(
  env: LiftEnv,
  owner: TrackOwner,
  valueType: TrackValueType,
  keys: readonly LiftKeyData[],
  importedLeadIn: boolean,
): TrackId {
  const id = env.nextId() as TrackId;
  env.tracks.set(id, importedLeadIn ? { id, owner, valueType, importedLeadIn: true } : { id, owner, valueType });
  for (const k of keys) {
    const kid = env.nextId() as KeyId;
    const rec: Key = {
      id: kid,
      track: id,
      t: k.t,
      vIn: k.vIn,
      vOut: k.vOut,
      ease: k.ease,
      link: k.link,
      ...(k.src !== undefined ? { src: k.src } : {}),
    };
    env.keys.set(kid, rec);
  }
  return id;
}

/** Track of one line property list (skipped events reported at `path`). */
function liftList(
  env: LiftEnv,
  line: LineId,
  li: number,
  prop: LineProp,
  layer: number,
  events: readonly IrEvent<TrackValue>[],
  path: string,
): void {
  // Lower's lone-key and trailing events read back without the sidecar lift to the keys they came from (UX-026).
  const r = liftLineEvents(events, prop, env.speedMode, true);
  for (const i of r.skipped) env.report('changed', 'ir.lift.eventSkipped', undefined, `${path}[${String(i)}]`, li);
  if (r.keys.length === 0) return;
  const first = r.keys[0] as LiftKeyData;
  const leadIn = requiresLeadInKey(prop) && Beats.cmp(first.t, Beats.ZERO) > 0;
  addTrack(env, { kind: 'line', line, prop, layer }, VALUE_TYPE[prop], r.keys, leadIn);
}

function trimNulls<T>(arr: (T | null)[]): (T | null)[] {
  let n = arr.length;
  while (n > 0 && arr[n - 1] === null) n--;
  return arr.slice(0, n);
}

function layerList(layer: IrLayer, prop: LineProp): readonly IrEvent<TrackValue>[] | undefined {
  switch (prop) {
    case 'alpha':
      return layer.alphaEvents;
    case 'moveX':
      return layer.moveXEvents;
    case 'moveY':
      return layer.moveYEvents;
    case 'rotate':
      return layer.rotateEvents;
    case 'speed':
      return layer.speedEvents;
    default:
      return undefined;
  }
}

function extList(ext: IrExtended, prop: LineProp): readonly IrEvent<TrackValue>[] | readonly [] | undefined {
  switch (prop) {
    case 'scaleX':
      return ext.scaleXEvents;
    case 'scaleY':
      return ext.scaleYEvents;
    case 'color':
      return ext.colorEvents;
    case 'text':
      return ext.textEvents;
    case 'incline':
      return ext.inclineEvents;
    case 'paint':
      return ext.paintEvents;
    case 'gif':
      return ext.gifEvents;
    default:
      return undefined;
  }
}

interface LayersOut {
  readonly layerSlots: (LayerSlotStyle | null)[];
  readonly layerExtra: (Passthrough | null)[];
  readonly nullLayers: (readonly string[] | null)[];
}

function liftLayers(env: LiftEnv, ir: IrLine, line: LineId, li: number): LayersOut {
  const layerSlots: (LayerSlotStyle | null)[] = [];
  const layerExtra: (Passthrough | null)[] = [];
  const nullLayers: (readonly string[] | null)[] = [];
  ir.eventLayers.forEach((slot, i) => {
    if (slot === null) {
      layerSlots.push('null');
      layerExtra.push(null);
      nullLayers.push(null);
      return;
    }
    let hasTracks = false;
    for (const prop of LAYER_PROPS_IN_ORDER) {
      const list = layerList(slot, prop);
      if (list === undefined) continue;
      hasTracks = true;
      liftList(
        env,
        line,
        li,
        prop,
        i,
        list,
        `judgeLineList[${String(li)}].eventLayers[${String(i)}].${listKeyOf(prop)}`,
      );
    }
    layerSlots.push(hasTracks ? null : 'object');
    layerExtra.push(slot.extra.length > 0 ? slot.extra : null);
    nullLayers.push(slot.nullKeys.length > 0 ? slot.nullKeys : null);
  });
  return { layerSlots: trimNulls(layerSlots), layerExtra: trimNulls(layerExtra), nullLayers: trimNulls(nullLayers) };
}

interface ExtendedOut {
  readonly ignored: IgnoredData[];
  readonly nullKeys: readonly string[];
  readonly extra: Passthrough;
  readonly gifAutoLoop: boolean;
}

function liftExtended(env: LiftEnv, ir: IrLine, ext: IrExtended, line: LineId, li: number): ExtendedOut {
  const ignored: IgnoredData[] = [];
  let gifAutoLoop = false;
  const base = `judgeLineList[${String(li)}].extended`;
  for (const prop of EXT_PROPS_IN_ORDER) {
    const list = extList(ext, prop);
    if (list === undefined) continue;
    const key = listKeyOf(prop);
    const path = `${base}.${key}`;
    const toJson = (): Json => (list as readonly IrEvent<TrackValue>[]).map((e) => eventJson(e as IrEvent<number>));
    if ((prop === 'text' || prop === 'paint') && ir.textureKind !== 'builtin') {
      // Phira ignores text / paint on a textured line: kept verbatim in place.
      ignored.push({ key, value: toJson(), writeBack: 'extended' });
      continue;
    }
    if (prop === 'gif') {
      if (ir.textureKind === 'builtin') {
        ignored.push({ key, value: toJson(), writeBack: 'extended' });
        continue;
      }
      if (ir.textureKind !== 'gif') {
        // Phira decodes the texture as a GIF as soon as gifEvents exists: a PNG / JPG fails the whole chart.
        ignored.push({ key, value: toJson(), writeBack: 'sidecar' });
        env.report('changed', 'ir.lift.gifOnNonGif', { texture: ir.Texture }, path, li);
        continue;
      }
      if (list.length === 0) {
        gifAutoLoop = true;
        continue;
      }
    }
    if (list.length === 0) continue;
    liftList(env, line, li, prop, 0, list as readonly IrEvent<TrackValue>[], path);
  }
  for (const [key, value] of ext.ignoredLists) {
    if (ignored.some((d) => d.key === key)) continue;
    ignored.push({ key, value, writeBack: 'extended' });
  }
  return { ignored, nullKeys: ext.nullKeys, extra: ext.extra, gifAutoLoop };
}

const NOTE_KIND: Readonly<Record<IrNote['type'], NoteKind>> = { 1: 'tap', 2: 'hold', 3: 'flick', 4: 'drag' };

const BUILTIN_HITSOUNDS: ReadonlyMap<string, 'tap' | 'drag' | 'flick'> = new Map([
  ['tap.mp3', 'tap'],
  ['drag.mp3', 'drag'],
  ['flick.mp3', 'flick'],
]);

function hitsoundIn(env: LiftEnv, name: string | undefined): Hitsound | null {
  if (name === undefined) return null;
  const builtin = BUILTIN_HITSOUNDS.get(name);
  if (builtin !== undefined) return { kind: 'builtin', sound: builtin };
  return { kind: 'asset', asset: env.assetIdForPath(name) };
}

function liftNote(env: LiftEnv, n: IrNote, line: LineId, li: number, ni: number): Note | null {
  const s = tripleIn(n.startTime);
  const e = tripleIn(n.endTime);
  if (s === null || e === null) {
    env.report('changed', 'ir.lift.noteSkipped', undefined, `judgeLineList[${String(li)}].notes[${String(ni)}]`, li);
    return null;
  }
  const kind = NOTE_KIND[n.type];
  const src: { -readonly [K in keyof NoteSrc]: NoteSrc[K] } = {};
  if (s.raw !== undefined) src.rawStart = s.raw;
  let end = s.beat;
  if (kind === 'hold') {
    end = Beats.max(s.beat, e.beat);
    if (e.raw !== undefined && Beats.eq(end, e.beat)) src.rawEnd = e.raw;
  } else if (!tripleEquals(n.endTime, n.startTime)) {
    src.nonHoldEnd = n.endTime;
  }
  if (n.above !== 1 && n.above !== 2) src.aboveRaw = n.above;
  if (n.isFake !== 0 && n.isFake !== 1) src.isFakeRaw = n.isFake;
  if (n.extra.length > 0) src.extra = n.extra;
  const id = env.nextId() as NoteId;
  const note: Note = {
    id,
    line,
    kind,
    t: s.beat,
    end,
    x: n.positionX,
    above: n.above === 1,
    fake: n.isFake !== 0,
    speed: n.speed,
    yOffset: n.yOffset,
    size: n.size,
    alpha: n.alpha,
    visibleTime: n.visibleTime === RPE_VISIBLE_ALWAYS ? null : n.visibleTime,
    hitsound: hitsoundIn(env, n.hitsound),
    tint: n.tint === undefined ? null : ([...n.tint] as unknown as RGB),
    tintHitEffects: n.tintHitEffects === undefined ? null : ([...n.tintHitEffects] as unknown as RGB),
    judgeArea: n.judgeArea ?? null,
    gen: null,
    ...(Object.keys(src).length > 0 ? { src } : {}),
  };
  env.notes.set(id, note);
  return note;
}

function controlOrNull(points: IrLine['posControl']): ControlCurve | null {
  return points === undefined ? null : controlIn(points);
}

/** Resolution of chart-level references for a line. */
export interface LineRefs {
  /** Line id by export index. */
  readonly lineIds: readonly LineId[];
  /**
   * Group by judgeLineGroup index: an id, or null for "Default" at index 0 (no group) and unused gap entries.
   * An index past the end is reported and lifted as no group.
   */
  readonly groupIds: readonly (GroupId | null)[];
}

/** Lifts one line (its tracks, keys and notes go into the environment's tables). */
export function liftLine(env: LiftEnv, source: IrLine, li: number, id: LineId, refs: LineRefs): Line {
  // Lower's padding read back from a file is not lifted to keys: the line was an editor line (UX-026).
  const unpadded = unpadEditorLine(source);
  const ir = unpadded ?? source;
  const origin = unpadded === null ? 'import' : 'editor';
  const texture = ir.Texture === 'line.png' ? null : env.assetIdForPath(ir.Texture);
  let parent: LineId | null = null;
  if (ir.father !== -1) {
    // A line that is its own parent is a one-line cycle: lift.ts#breakCycles breaks and reports it.
    const p = refs.lineIds[ir.father];
    if (p !== undefined) parent = p;
    else
      env.report(
        'changed',
        'ir.lift.fatherMissing',
        { line: li, father: ir.father },
        `judgeLineList[${String(li)}].father`,
        li,
      );
  }
  const lifted = refs.groupIds[ir.Group];
  const group: GroupId | null = lifted ?? null;
  if (lifted === undefined) {
    env.report(
      'rpeOnly',
      'ir.lift.groupMissing',
      { line: li, group: ir.Group },
      `judgeLineList[${String(li)}].Group`,
      li,
    );
  }

  const layers = liftLayers(env, ir, id, li);
  let ext: ExtendedOut = { ignored: [], nullKeys: [], extra: [], gifAutoLoop: false };
  if (ir.extended !== null && ir.extended !== 'absent') ext = liftExtended(env, ir, ir.extended, id, li);
  ir.notes.forEach((n, ni) => liftNote(env, n, id, li, ni));

  const rpe: LineRpeData = {
    ...Defaults.lineRpeData(origin),
    bpmfactor: ir.bpmfactor,
    anchor: [ir.anchor[0], ir.anchor[1]],
    isCoverRaw: ir.isCover !== 0 && ir.isCover !== 1 ? ir.isCover : null,
    skewControl: ir.skewControl ?? null,
    layerSlots: layers.layerSlots,
    extended: ir.extended === 'absent' ? 'absent' : ir.extended === null ? 'null' : 'object',
    notesKey: ir.notesKey,
    ignored: ext.ignored,
    nullKeys: { extended: ext.nullKeys, layers: layers.nullLayers },
    extra: ir.extra,
    extendedExtra: ext.extra,
    layerExtra: layers.layerExtra,
  };
  return {
    id,
    name: ir.Name,
    parent,
    rotateWithParent: ir.rotateWithFather,
    zOrder: ir.zOrder,
    cover: ir.isCover === 1,
    texture,
    attachUI: ir.attachUI,
    gifAutoLoop: ext.gifAutoLoop,
    group,
    controls: {
      pos: controlOrNull(ir.posControl),
      alpha: controlOrNull(ir.alphaControl),
      size: controlOrNull(ir.sizeControl),
      y: controlOrNull(ir.yControl),
    },
    origin,
    editor: Defaults.lineEditorData(),
    rpe,
  };
}
